import useStyles from 'isomorphic-style-loader/useStyles';
import { UserRound } from 'lucide-react';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import type { PopupProps } from 'semantic-ui-react';
import { Header, Icon, Popup } from 'semantic-ui-react';

import KeyValueList from '@components/key-value-list';
import type { KeyValueItem } from '@components/key-value-list/key-value-list';

import s from './contact-card.scss';

export interface ContactCardProps {
  /**
   * Full name of the contact
   */
  name: string;
  /**
   * Optional email address of the contact
   */
  email?: string;
  /**
   * Optional phone number of the contact
   */
  phone?: string;
  /**
   * Custom trigger element. The contact info popup positions relative
   * to whatever node is passed in. When omitted, falls back to a Lucide
   * user icon + name composition.
   */
  children?: ReactNode;
  /**
   * Popup position; defaults to `top center`.
   */
  position?: PopupProps['position'];
  /**
   * Optional Popper offset for the popup; no offset is applied when omitted.
   */
  offset?: PopupProps['offset'];
}

/**
 * Renders a contact name decorated with an icon, and reveals the full contact
 * info in a popup card on hover. The trigger truncates the name with an
 * ellipsis when it overflows its container, and the popup always lists the
 * full name (plus phone / email when present) so the truncated value remains
 * discoverable.
 *
 * Hover handling sits on the entire trigger element (icon + name for the
 * default trigger, or whatever node is passed as children), so users can
 * mouse anywhere over the row to surface the popup.
 */
export const ContactCard = ({
  name,
  email,
  phone,
  children,
  position = 'top center',
  offset,
}: ContactCardProps) => {
  useStyles(s);

  const trigger = children ?? (
    <span className={s.trigger}>
      <UserRound className={s.triggerIcon} />
      <span className={s.triggerName}>{name}</span>
    </span>
  );

  const infoItems = useMemo<KeyValueItem[]>(() => {
    const items: KeyValueItem[] = [{ key: 'Name', value: name }];

    if (phone) items.push({ key: 'Phone Number', value: phone });
    if (email) items.push({ key: 'Email', value: email });

    return items;
  }, [name, email, phone]);

  return (
    <Popup
      className={s.popup}
      position={position}
      offset={offset}
      role="tooltip"
      trigger={trigger}
      header={
        <Header as="h6" className={s.header}>
          <Icon name="vcard" color="teal" />
          <Header.Content>Contact</Header.Content>
        </Header>
      }
      content={
        <KeyValueList
          className={s.content}
          items={infoItems}
          labelWidthRem={8}
          justify
        />
      }
      flowing
      hoverable
    />
  );
};

export default ContactCard;
